import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Loader2 } from 'lucide-react';
import {
  EvaluationTest,
  EvaluationProviderConfig as EvaluationProviderConfigType,
  PromptMode,
} from '../types/evaluation';
import { EvaluationApiService } from '../services/evaluationApi';
import { EvaluationProviderConfig as EvaluationProviderConfigComponent } from './EvaluationProviderConfig';

export const BatchRunPage = () => {
  const navigate = useNavigate();
  const evaluationApi = EvaluationApiService.getInstance();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [promptFileError, setPromptFileError] = useState<string | null>(null);
  const [promptFileName, setPromptFileName] = useState<string | null>(null);
  const [customPrompts, setCustomPrompts] = useState<string[] | null>(null);
  const [showPromptPreview, setShowPromptPreview] = useState(false);

  // Form state
  const [testName, setTestName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [providerConfigs, setProviderConfigs] = useState<EvaluationProviderConfigType[]>([]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!testName.trim()) {
      errors.testName = 'Test name is required';
    }

    if (!customPrompts || customPrompts.length === 0) {
      errors.promptFile = 'JSON file upload is required';
    }

    if (providerConfigs.length < 2) {
      errors.providerConfigs = 'At least 2 provider configurations are required';
    }

    providerConfigs.forEach((config, index) => {
      if (!config.name.trim()) {
        errors[`provider_${index}_name`] = `Provider ${index + 1} name is required`;
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePromptFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPromptFileError(null);
    setPromptFileName(file.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target?.result as string);
        if (!Array.isArray(parsed) || !parsed.every((item) => typeof item === 'string')) {
          throw new Error('JSON must be an array of strings');
        }
        setCustomPrompts(parsed);
      } catch {
        setPromptFileError('Invalid JSON format. Expected an array of strings.');
        setCustomPrompts(null);
        setPromptFileName(null);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleClearPrompts = () => {
    setCustomPrompts(null);
    setPromptFileError(null);
    setPromptFileName(null);
    setShowPromptPreview(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const modifiedConfig: EvaluationTest = {
        id: crypto.randomUUID(),
        name: testName.trim(),
        purpose: purpose.trim(),
        mode: 'Open' as PromptMode,
        providerConfigs,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        creatorUserId: 'batch-user',
        isDeleted: false,
        canEdit: false,
        testOrigin: 'Batch',
      };

      const result = await evaluationApi.startBatchRun(modifiedConfig, customPrompts!);

      alert(`Batch run started with ${result.promptCount} prompts. Results will appear as they complete.`);
      navigate(`/opinions/evaluation/${result.testId}/analytics`);
    } catch (err: any) {
      setError(err.message || 'Failed to start batch run');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    navigate('/opinions/evaluation');
  };

  return (
    <div data-component="BatchRunPage" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Batch Evaluation Run</h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {error && (
            <div data-flow="error" className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {Object.keys(validationErrors).length > 0 && (
            <div data-flow="validation-errors" className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-yellow-800 font-medium mb-2">Please fix the following errors:</p>
              <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
                {Object.values(validationErrors).map((err, index) => (
                  <li key={index}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Custom Prompts */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Custom Prompts</h3>
              <div className="space-y-3">
                <div>
                  <label htmlFor="promptFile" className="block text-sm font-medium text-gray-700 mb-1">
                    Upload a JSON file with an array of prompt strings *
                  </label>
                  <input
                    id="promptFile"
                    type="file"
                    accept=".json"
                    onChange={handlePromptFileUpload}
                    disabled={isSubmitting}
                    className={`block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50 ${
                      validationErrors.promptFile ? 'border-red-500' : ''
                    }`}
                  />
                </div>

                {promptFileError && (
                  <p className="text-sm text-red-600">{promptFileError}</p>
                )}

                {customPrompts && !promptFileError && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <p className="text-sm text-green-800">
                        {customPrompts.length} prompts loaded from {promptFileName}
                      </p>
                      <button
                        type="button"
                        onClick={handleClearPrompts}
                        disabled={isSubmitting}
                        className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                      >
                        Clear
                      </button>
                    </div>

                    {customPrompts.length > 0 && (
                      <div className="mt-2">
                        <button
                          type="button"
                          onClick={() => setShowPromptPreview(!showPromptPreview)}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          {showPromptPreview ? 'Hide preview' : 'Show preview'}
                        </button>

                        {showPromptPreview && (
                          <div className="mt-2 bg-white border border-green-200 rounded p-2 max-h-40 overflow-y-auto">
                            {customPrompts.slice(0, 5).map((prompt, index) => (
                              <p key={index} className="text-xs text-gray-600 border-b border-gray-100 last:border-0 pb-1 mb-1 last:pb-0 last:mb-0">
                                {prompt.length > 100 ? `${prompt.substring(0, 100)}...` : prompt}
                              </p>
                            ))}
                            {customPrompts.length > 5 && (
                              <p className="text-xs text-gray-500 mt-1">
                                and {customPrompts.length - 5} more...
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Basic Information</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="testName" className="block text-sm font-medium text-gray-700 mb-1">
                    Test Name *
                  </label>
                  <input
                    id="testName"
                    type="text"
                    value={testName}
                    onChange={(e) => setTestName(e.target.value)}
                    disabled={isSubmitting}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 ${
                      validationErrors.testName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {validationErrors.testName && (
                    <p className="text-sm text-red-600 mt-1">{validationErrors.testName}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="purpose" className="block text-sm font-medium text-gray-700 mb-1">
                    Purpose
                  </label>
                  <textarea
                    id="purpose"
                    value={purpose}
                    onChange={(e) => setPurpose(e.target.value)}
                    disabled={isSubmitting}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  />
                </div>
              </div>
            </div>

            {/* Provider Configurations */}
            <EvaluationProviderConfigComponent
              configs={providerConfigs}
              onConfigsChange={setProviderConfigs}
              disabled={isSubmitting}
              validationErrors={validationErrors}
              mode="multi"
            />

            {/* Footer */}
            <div className="pt-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Starting Batch Run...
                  </>
                ) : (
                  'Start Batch Run'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};